import {FC, useMemo} from 'react'
import dayjs from 'dayjs'
import {Text} from '@/components/ui/text/Text'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Divider} from '@/components/ui/divider/Divider'
import {ActivityLog, ActionType} from '@/features/entity-activity-logs/types'
import {ProjectLifecycleContext, getProjectLifecycleLabel} from '@/features/entity-activity-logs/utils'
import {useTranslation} from '@/translations/i18n'

interface ActivityLogItemProps {
    log: ActivityLog
    showDivider?: boolean
    /** Only provided by the project logs tab: lets lifecycle operations be named instead of diffed. */
    projectLifecycle?: ProjectLifecycleContext
}

const getActionLabel = (action: ActionType): string => {
    switch (action) {
        case 'create':
            return 'Creazione'
        case 'update':
            return 'Aggiornamento'
        case 'delete':
            return 'Cancellazione'
        default:
            return action
    }
}

const getUserDisplayName = (log: ActivityLog): string => {
    return log.user ? `${log.user.firstName} ${log.user.lastName}` : log.userId
}

const getChangesSummary = (log: ActivityLog): string => {
    if (log.action === 'create') return 'Entita creata'
    if (log.action === 'delete') return 'Entita cancellata'

    if (log.action === 'update' && log.oldData && log.newData) {
        const changedFields = Object.keys(log.newData).filter(
            key => JSON.stringify(log.oldData?.[key]) !== JSON.stringify(log.newData?.[key])
        )
        return changedFields.length === 0
            ? `Alcune informazioni sono state modificate` //info not present in the json
            : `${changedFields.length} campi sono stati modificati`
    }
    return ''
}

export const ActivityLogItem: FC<ActivityLogItemProps> = ({log, showDivider = true, projectLifecycle}) => {
    const {t} = useTranslation()
    const formattedDate = useMemo(() => new Date(log.createdAt).toLocaleString(), [log.createdAt])
    const userDisplayName = useMemo(() => getUserDisplayName(log), [log])
    const lifecycleLabel = useMemo(() => getProjectLifecycleLabel(log, projectLifecycle), [log, projectLifecycle])

    const actionLabel = useMemo(
        () => (lifecycleLabel ? t(lifecycleLabel) : getActionLabel(log.action)),
        [lifecycleLabel, log.action, t]
    )

    const changesSummary = useMemo(() => {
        if (!lifecycleLabel) return getChangesSummary(log)

        // Name the source project instead of diffing two different rows.
        const sourceCode = log.oldData?.code
        const sourceStartDate = log.oldData?.startDate

        return sourceCode && sourceStartDate
            ? t('projects:log_derived_from_project', {
                  code: sourceCode,
                  date: dayjs(sourceStartDate).format('DD/MM/YYYY')
              })
            : ''
    }, [lifecycleLabel, log, t])

    return (
        <Flexbox direction="column" gap={1} fullWidth>
            <Text palette="neutral" shade="900" size="textSm" weight="500">
                {actionLabel}
            </Text>
            <Text palette="neutral" shade="500" size="textXs">
                {formattedDate} - {userDisplayName}
            </Text>
            {!!changesSummary && (
                <Text palette="neutral" shade="700" size="textXs">
                    {changesSummary}
                </Text>
            )}
            {showDivider && <Divider direction="horizontal" startSpacing={2} />}
        </Flexbox>
    )
}
